/**
 * SessionExpiredView
 *
 * Rendered by the root `ErrorBoundary` when an auth-expiry error is detected
 * (see `lib/auth-error-detection.ts`). Replaces the generic "Oops!" surface
 * with a clear re-login prompt and CTA, preserving the user's intended
 * destination via `returnTo` query string.
 */

import { Link, useLocation } from "react-router";

interface SessionExpiredViewProps {
  /**
   * Override for the `returnTo` value. When omitted, the component reads
   * the current location from React Router (with a `window.location`
   * fallback when no router context is available).
   */
  return_to?: string;
}

export function SessionExpiredView({ return_to }: SessionExpiredViewProps) {
  // React Router provides location even on error boundaries inside a router.
  // Outside a router, `useLocation` would throw — but root.tsx renders the
  // boundary inside the router, and tests wrap in a MemoryRouter.
  const location = useLocation();
  const path =
    return_to ?? `${location.pathname}${location.search ?? ""}`;
  const target = build_login_url(path);

  return (
    <main className="flex flex-col items-center justify-center min-h-[50vh] px-4 pt-16 text-center container mx-auto">
      <h1 className="text-xl font-bold">Your session has expired</h1>
      <p className="text-sm text-base-content/60 mt-2 max-w-sm">
        Please log back in to continue.
      </p>
      <Link to={target} className="btn btn-primary mt-6">
        Log in
      </Link>
    </main>
  );
}

function build_login_url(path: string): string {
  // Don't bounce the user right back to /login.
  if (!path || path === "/login" || path.startsWith("/login?")) {
    return "/login";
  }
  return `/login?returnTo=${encodeURIComponent(path)}`;
}
